<script lang="ts" setup>
import LabelTag from "~/components/form/LabelTag.vue";
import InputTag from "~/components/form/InputTag.vue";
import SelectTag from "~/components/form/SelectTag.vue";
import CheckboxTag from "~/components/form/CheckboxTag.vue";

const config = useRuntimeConfig();
const router = useRouter();

// Properties
const props = defineProps({
    showCheckboxSave: {
        type: Boolean,
        default: false,
    },
    showCheckboxDefaultPayment: {
        type: Boolean,
        default: false,
    },
    initialData: {
        type: Object,
        default: {},
    },
    isEdit: {
        type: Boolean,
        default: false,
    },
    inCheckout: {
        type: Boolean,
        default: false,
    },
});

// Data
const store = useCheckoutStore();
const accountTypes = { checking: "Checking Account", savings: "Savings Account", other: "Other" };

const form = ref({
    id: "",
    paymentType: "ach",
    paymentProfileId: 0,
    accountHolderName: "",
    accountNumber: "",
    accountNumberConfirm: "",
    routingNumber: "",
    accountType: "",
    agreeToTermsAndConditions: false,
    saveACH: false, // Member only + New card
    setAsDefault: "", // Member only
});

// Functions
function setFormData() {
    if (props.initialData && props.initialData.payment && props.initialData.payment.accountHolderName) {
        const data = props.initialData.payment;
        form.value.id = props.initialData.id;

        form.value.paymentProfileId = data.paymentProfileId ?? 0;
        form.value.accountHolderName = data.accountHolderName;
        form.value.accountNumber = props.initialData.id ? "" : data.accountNumber; // Force user to re-enter
        form.value.accountNumberConfirm = data.accountNumberConfirm;
        form.value.routingNumber = props.initialData.id ? "" : data.routingNumber; // Force user to re-enter
        form.value.accountType = data.accountType;

        form.value.agreeToTermsAndConditions = data.agreeToTermsAndConditions;
        form.value.saveACH = data.saveACH;
        form.value.setAsDefault = data.setAsDefault;
    }
}

function validateAndSave() {
    let status = false;
    const data = form.value;
    if (
        data &&
        data.accountHolderName &&
        data.accountNumber &&
        data.accountNumberConfirm &&
        data.routingNumber &&
        data.accountType
    ) {
        status = true;
        if (props.inCheckout) {
            store.updatePayment(data);
        }
    }

    // This is being used in checkout only
    store.updatePlaceOrderEnabled(props.inCheckout ? status : false);
}

onMounted(() => {
    setFormData();
});
onUpdated(() => {
    validateAndSave();
});

defineExpose({ form });
</script>

<template>
    <div>
        <div v-if="isEdit">
            <H3Heading size="text-lg">Edit ACH</H3Heading>
            <div class="mb-8 flex">
                <div class="w-fit flex-none pr-2">
                    <img src="/images/checkout/ACH.svg" alt="ACH" title="ACH" class="mr-1 inline-block w-8" />
                </div>
                <div class="flex-1">
                    <span class="font-black">Bank ending in {{ initialData.payment.accountNumber }}</span>
                </div>
            </div>
        </div>

        <div>
            <LabelTag for="accountHolderName">Account Holders Name *</LabelTag>
            <InputTag
                id="accountHolderName"
                v-model="form.accountHolderName"
                placeholder="Enter name as it appears on account"
                class="w-full rounded-sm"
                required
            />
        </div>
        <div class="mt-4 md:flex">
            <div class="flex-1 md:pr-4">
                <LabelTag for="accountNumber">Account Number *</LabelTag>
                <InputTag
                    id="accountNumber"
                    v-model="form.accountNumber"
                    placeholder="Up to 17 Digit Account Numbers"
                    class="w-full rounded-sm"
                    maxlength="17"
                    required
                />
            </div>
            <div class="flex-1">
                <LabelTag for="accountNumberConfirm">Confirm Account Number *</LabelTag>
                <InputTag
                    id="accountNumberConfirm"
                    v-model="form.accountNumberConfirm"
                    placeholder="Confirm Account Number"
                    class="w-full rounded-sm"
                    maxlength="17"
                    required
                />
            </div>
        </div>
        <div class="mt-4 md:flex">
            <div class="flex-1 md:pr-4">
                <LabelTag for="routingNumber">Routing Number *</LabelTag>
                <InputTag
                    id="routingNumber"
                    v-model="form.routingNumber"
                    placeholder="9 Digit Routing Number"
                    class="w-full rounded-sm"
                    maxlength="9"
                    required
                />
            </div>
            <div class="flex-1">
                <LabelTag for="accountType">Account Type *</LabelTag>
                <SelectTag id="accountType" v-model="form.accountType" class="w-full" required>
                    <option value="">Select</option>
                    <option v-for="(val, key) in accountTypes" :value="key">{{ val }}</option>
                </SelectTag>
            </div>
        </div>

        <div v-if="showCheckboxSave || showCheckboxDefaultPayment" class="mt-4 text-sm">
            <!-- Logged in user + Show on add -->
            <CheckboxTag v-if="showCheckboxSave" v-model="form.saveACH" :value="true"
                >Save this ACH for future purchases
            </CheckboxTag>
            <!-- Logged in user + Show on add/edit -->
            <CheckboxTag v-if="showCheckboxDefaultPayment" v-model="form.setAsDefault" :value="true" class="mt-2"
                >Set as my default payment method
            </CheckboxTag>
        </div>
    </div>
</template>
